import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, AlertTriangle, X, Info } from 'lucide-react';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS: Record<ToastVariant, number> = {
  success: 5000,
  info: 5000,
  error: 8000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = idRef.current++;
      setToasts((cur) => [...cur, { id, variant, message }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS[variant]);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="fixed top-4 right-4 z-[200] flex w-[calc(100vw-2rem)] max-w-[360px] flex-col gap-2 pointer-events-none">
          <AnimatePresence>
            {toasts.map((t) => (
              <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const styles: Record<ToastVariant, { bg: string; border: string; icon: ReactNode }> = {
    success: {
      bg: 'bg-white',
      border: 'border-brand-accent',
      icon: <CheckCircle2 className="w-5 h-5 text-brand-deep" />,
    },
    error: {
      bg: 'bg-white',
      border: 'border-red-300',
      icon: <AlertTriangle className="w-5 h-5 text-red-600" />,
    },
    info: {
      bg: 'bg-white',
      border: 'border-sky-300',
      icon: <Info className="w-5 h-5 text-sky-600" />,
    },
  };
  const s = styles[toast.variant];
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 16, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 16, scale: 0.96 }}
      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      className={`pointer-events-auto ${s.bg} ${s.border} border rounded-xl shadow-lg px-4 py-3 flex items-start gap-3`}
      role="status"
    >
      <span className="shrink-0 mt-0.5">{s.icon}</span>
      <p className="flex-1 text-sm text-brand-primary font-medium leading-snug">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 text-gray-400 hover:text-brand-primary"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export type { ToastApi };
